import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, Subscription, fromEvent} from 'rxjs';
import {Capacitor} from '@capacitor/core';
import {Network} from '@capacitor/network';

export type NetworkConnectionType = 'wifi' | 'cellular' | 'none' | 'unknown';

export interface NetworkStatusState {
  connected: boolean;
  connectionType: NetworkConnectionType;
}

@Injectable({providedIn: 'root'})
export class NetworkStatusService implements OnDestroy {
  private readonly statusSubject = new BehaviorSubject<NetworkStatusState>(this.getBrowserStatus());
  private initialized = false;
  private browserSubscriptions: Subscription[] = [];
  private networkListener: {remove: () => Promise<void>} | null = null;

  get status$(): Observable<NetworkStatusState> {
    return this.statusSubject.asObservable();
  }

  get isOffline$(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const sub = this.status$.subscribe((state) => observer.next(!state.connected));
      return () => sub.unsubscribe();
    });
  }

  get isOnline$(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const sub = this.status$.subscribe((state) => observer.next(state.connected));
      return () => sub.unsubscribe();
    });
  }

  get connectionType$(): Observable<NetworkConnectionType> {
    return new Observable<NetworkConnectionType>((observer) => {
      const sub = this.status$.subscribe((state) => observer.next(state.connectionType));
      return () => sub.unsubscribe();
    });
  }

  get snapshot(): NetworkStatusState {
    return this.statusSubject.value;
  }

  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      this.publishStatus(true, 'unknown');
      return;
    }

    if (Capacitor.getPlatform() !== 'web') {
      const status = await Network.getStatus();
      this.publishStatus(status.connected, status.connectionType as NetworkConnectionType);
      this.networkListener = await Network.addListener('networkStatusChange', (state) => {
        this.publishStatus(state.connected, state.connectionType as NetworkConnectionType);
      });
      return;
    }

    this.publishStatus(navigator.onLine, navigator.onLine ? 'unknown' : 'none');
    this.browserSubscriptions.push(
      fromEvent(window, 'online').subscribe(() => this.publishStatus(true, 'unknown')),
      fromEvent(window, 'offline').subscribe(() => this.publishStatus(false, 'none')),
    );
  }

  isOffline(): boolean {
    return !this.statusSubject.value.connected;
  }

  ngOnDestroy(): void {
    this.browserSubscriptions.forEach((sub) => sub.unsubscribe());
    this.browserSubscriptions = [];

    if (this.networkListener) {
      void this.networkListener.remove();
      this.networkListener = null;
    }
  }

  private publishStatus(connected: boolean, connectionType: NetworkConnectionType): void {
    this.statusSubject.next({
      connected,
      connectionType: connected ? (connectionType || 'unknown') : 'none'
    });
  }

  private getBrowserStatus(): NetworkStatusState {
    if (typeof navigator === 'undefined') {
      return {connected: true, connectionType: 'unknown'};
    }
    return {
      connected: navigator.onLine,
      connectionType: navigator.onLine ? 'unknown' : 'none'
    };
  }
}
